import type { UIRunResult } from '@sorrem-client/types';
import { resolveEssenceExchangeValue, resolveVictoryItemCount } from '@sorrem/shared';
import { describeDefeatItemLoss, PARTY_WIPE_SUMMARY } from './defeat-item-loss-message';

export interface RunResultDescription {
    /** The results notice's heading. */
    title: string;
    /** How the run ended and what it meant for the character; the results notice's body. */
    message: string;
}

/** What `describeRunResult` reads of a run's end. */
export type DescribableRunResult = Pick<UIRunResult, 'outcome' | 'reason' | 'winnings' | 'carriedItemLosses' | 'isDefeatPenaltyWaived'>;

/** What the character banked on a victory. */
function describeWinnings(winnings: DescribableRunResult['winnings'], characterId: string): string {
    if (!winnings) {
        return 'You banked Essence.';
    }
    const essenceTotal = resolveEssenceExchangeValue(winnings.essence[characterId] ?? {});
    const isFullClear = winnings.fullClearCharacterIds.includes(characterId);
    const fullClearNote = isFullClear ? ' (full clear bonus applied)' : '';
    // `itemCount` is the crucible's; a full clear earns one more.
    const itemCount = resolveVictoryItemCount(winnings.itemCount, isFullClear);
    const itemNote = itemCount > 0
        ? ` You also received ${itemCount} random item${itemCount === 1 ? '' : 's'} in your character inventory.`
        : '';
    return `You banked ${essenceTotal.toLocaleString()} Essence${fullClearNote}.${itemNote}`;
}

function describeRunResultTitle(outcome: DescribableRunResult['outcome']): string {
    return outcome === 'victory'
        ? 'Crucible Completed'
        : outcome === 'abandoned'
            ? 'Crucible Unavailable'
            : 'Crucible Failed';
}

function describeRunResultMessage(result: DescribableRunResult, characterId: string): string {
    if (result.outcome === 'abandoned') {
        return 'The Trial host stopped before completion. No rewards or penalties were applied.';
    }
    if (result.reason === 'trial-heart-shattered') {
        return `The heart of the Crucible shattered. ${describeWinnings(result.winnings, characterId)}`;
    }
    if (result.reason === 'party-wiped') {
        return `The party was wiped. ${PARTY_WIPE_SUMMARY} ${describeDefeatItemLoss(result.carriedItemLosses?.[characterId], result.isDefeatPenaltyWaived === true)}`;
    }
    if (result.outcome === 'victory') {
        return `Crucible complete. ${describeWinnings(result.winnings, characterId)}`;
    }
    return 'Crucible failed. Collected essence was forfeited.';
}

/**
 * How a run ended, for the character `characterId`, as the results notice
 * shows it.
 */
export function describeRunResult(result: DescribableRunResult, characterId: string): RunResultDescription {
    return {
        title: describeRunResultTitle(result.outcome),
        message: describeRunResultMessage(result, characterId),
    };
}
